import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Layers,
  Sparkles,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Wand2,
  CheckCircle2,
  Square,
  RectangleVertical,
  GripVertical,
  Building2,
  X,
  Package,
  Save,
  BookmarkPlus,
  Check,
} from "lucide-react";
import { BrandLogoPickerDialog, BrandLogoImg } from "@/components/BrandLogoPickerDialog";
import { useBrandDetection } from "@/hooks/useBrandDetection";
import { computeAndromedaScore, getScoreColor } from "@/lib/andromedaScore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// ─── Bullet icons available for the per-bullet picker ─────────────────────
// Covers positive (benefits), negative (painpoints), warnings, ideas, etc.
const BULLET_ICONS = [
  { emoji: "✅", label: "Positiv / Vorteil" },
  { emoji: "❌", label: "Negativ / Painpoint" },
  { emoji: "⚠️", label: "Warnung / Risiko" },
  { emoji: "❓", label: "Frage / Unklar" },
  { emoji: "💡", label: "Idee / Tipp" },
  { emoji: "⭐", label: "Highlight / Premium" },
  { emoji: "🔥", label: "Hot / Trend" },
  { emoji: "👉", label: "Hinweis" },
  { emoji: "📌", label: "Pin / Merken" },
  { emoji: "💰", label: "Geld / Gehalt" },
  { emoji: "⏰", label: "Zeit" },
  { emoji: "🎯", label: "Ziel" },
];

// Detect the leading bullet icon (single emoji) in a string. Defaults to ✅.
function extractBulletIcon(text) {
  if (!text) return "✅";
  // Iterate icons; whichever the text starts with wins.
  for (const ic of BULLET_ICONS) {
    if (text.startsWith(ic.emoji)) return ic.emoji;
  }
  return "✅";
}

// Strip the leading icon + optional space, returning the plain text.
function stripBulletIcon(text) {
  if (!text) return "";
  for (const ic of BULLET_ICONS) {
    if (text.startsWith(ic.emoji)) {
      return text.slice(ic.emoji.length).replace(/^\s+/, "");
    }
  }
  return text;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TONE_OPTIONS = [
  { value: "professional", label: "Professionell", emoji: "💼", description: "Vertrauenswürdig, faktenbasiert" },
  { value: "viral", label: "Viral", emoji: "🔥", description: "Neugierig, scroll-stoppend" },
  { value: "clean", label: "Clean", emoji: "✨", description: "Minimalistisch, klar" },
];

const AD_TYPES = [
  { value: "recruiting", label: "Recruiting", description: "Mitarbeiter:innen gewinnen" },
  { value: "service", label: "Service", description: "Dienstleistung bewerben" },
  { value: "product", label: "Produkt", description: "Produkt verkaufen" },
  { value: "comparison", label: "Vergleich", description: "X vs Y" },
];

export default function CarouselGenerator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const authToken = typeof window !== "undefined" ? (localStorage.getItem("auth_token") || "") : "";

  // Step 1: Briefing
  const [briefing, setBriefing] = useState({
    ad_type: "recruiting",
    target_audience: "",
    description: "",
    painpoints: "",
    solution_promise: "",
    tone: "professional",
    job_role: "",
    cta: "Mehr erfahren",
    image_context: "",
  });

  // Brand Kits + Presets
  const [brandKits, setBrandKits] = useState([]);
  const [selectedBrandKit, setSelectedBrandKit] = useState(null);
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);

  // Step 2: Cover selection
  const [sessionId, setSessionId] = useState(null);
  const [coverVariants, setCoverVariants] = useState([]);
  const [selectedCoverId, setSelectedCoverId] = useState(null);
  const [customCoverHeadline, setCustomCoverHeadline] = useState("");
  const [customCoverSubText, setCustomCoverSubText] = useState("");

  // Step 3: Slides editing
  const [slides, setSlides] = useState([]);

  // Step 4: Visual config
  const [aspectRatio, setAspectRatio] = useState("4:5");
  const [brandColors, setBrandColors] = useState(["#1F2937", "#FFFFFF", "#3B82F6"]);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [persons, setPersons] = useState([]);
  const [logos, setLogos] = useState([]);
  const [selectedLogos, setSelectedLogos] = useState([]);
  const [selectedBrandSlugs, setSelectedBrandSlugs] = useState([]);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  // A/B/C-Test toggle: triggers backend to also generate variants
  // from the OTHER two cover-hooks.
  const [quickVariations, setQuickVariations] = useState(false);

  // Loading flags
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ───── Effects: load persons + logos for identity-lock / branding ─────
  useEffect(() => {
    axios.get(`${API}/persons`).then((r) => setPersons(r.data || [])).catch(() => {});
    axios.get(`${API}/logos`).then((r) => setLogos(r.data || [])).catch(() => {});
    axios.get(`${API}/brand-kits`).then((r) => setBrandKits(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/carousel/presets`).then((r) => setPresets(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // ───── Reuse: prefill from a previous carousel job (?reuse=<job_id>) ─────
  useEffect(() => {
    const reuseId = searchParams.get("reuse");
    if (!reuseId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/${reuseId}/wizard-data`);
        const { job_type, input_data = {}, briefing: br } = res.data || {};
        if (job_type !== "meta_carousel_generation") {
          toast.error("Dieser Job ist kein Karussell");
          return;
        }
        if (br) {
          setBriefing((prev) => ({ ...prev, ...br }));
        }
        if (input_data.aspect_ratio) setAspectRatio(input_data.aspect_ratio);
        if (Array.isArray(input_data.brand_colors) && input_data.brand_colors.length >= 2) {
          setBrandColors(input_data.brand_colors);
        }
        if (Array.isArray(input_data.selected_persons) && input_data.selected_persons.length > 0) {
          setSelectedPersonId(input_data.selected_persons[0]);
        }
        if (Array.isArray(input_data.selected_logos)) {
          setSelectedLogos(input_data.selected_logos.slice(0, 1));
        }
        if (Array.isArray(input_data.selected_brand_slugs)) {
          setSelectedBrandSlugs(input_data.selected_brand_slugs.slice(0, 1));
        }
        toast.success("Karussell-Einstellungen übernommen — passe an und generiere neu!");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Karussell-Reuse fehlgeschlagen");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ───── Brand Kit quick-apply ─────
  const applyBrandKit = useCallback((kit) => {
    if (!kit) return;
    setSelectedBrandKit(kit);
    // Colors -> brandColors (extract hex values)
    if (Array.isArray(kit.colors) && kit.colors.length > 0) {
      const hexes = kit.colors.map((c) => c.hex || c).filter(Boolean);
      if (hexes.length >= 2) setBrandColors(hexes.slice(0, 5));
    }
    // Logos
    if (Array.isArray(kit.logo_ids) && kit.logo_ids.length > 0) {
      setSelectedLogos([kit.logo_ids[0]]);
      setSelectedBrandSlugs([]);
    }
    // Briefing fields
    setBriefing((prev) => ({
      ...prev,
      cta: kit.default_cta || prev.cta,
      tone: ["professional", "viral", "clean"].includes((kit.tone || "").toLowerCase())
        ? kit.tone.toLowerCase()
        : prev.tone,
      image_context: kit.image_preferences || prev.image_context,
    }));
    toast.success(`Brand Kit „${kit.name}" angewendet`);
  }, []);

  // ───── Carousel Preset handlers ─────
  const applyPreset = useCallback((preset) => {
    const d = preset?.wizard_data || {};
    if (d.briefing) {
      setBriefing((prev) => ({ ...prev, ...d.briefing }));
    }
    if (d.aspect_ratio) setAspectRatio(d.aspect_ratio);
    if (Array.isArray(d.brand_colors) && d.brand_colors.length >= 2) setBrandColors(d.brand_colors);
    if (Array.isArray(d.selected_persons) && d.selected_persons.length > 0) {
      setSelectedPersonId(d.selected_persons[0]);
    }
    if (Array.isArray(d.selected_logos)) setSelectedLogos(d.selected_logos.slice(0, 1));
    if (Array.isArray(d.selected_brand_slugs)) setSelectedBrandSlugs(d.selected_brand_slugs.slice(0, 1));
    if (typeof d.quick_variations === "boolean") setQuickVariations(d.quick_variations);
    setShowPresets(false);
    toast.success(`Vorlage „${preset.name}" geladen`);
  }, []);

  const handleSavePreset = async () => {
    const name = presetName.trim();
    if (!name) {
      toast.error("Bitte einen Namen vergeben");
      return;
    }
    setSavingPreset(true);
    try {
      await axios.post(`${API}/carousel/presets`, {
        name,
        briefing,
        aspect_ratio: aspectRatio,
        brand_colors: brandColors.filter(Boolean),
        selected_persons: selectedPersonId ? [selectedPersonId] : [],
        selected_logos: selectedLogos,
        selected_brand_slugs: selectedBrandSlugs,
        quick_variations: quickVariations,
      });
      toast.success("Vorlage gespeichert");
      setSavePresetOpen(false);
      setPresetName("");
      const r = await axios.get(`${API}/carousel/presets`);
      setPresets(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingPreset(false);
    }
  };

  const handleDeletePreset = async (presetId, e) => {
    e?.stopPropagation();
    try {
      await axios.delete(`${API}/carousel/presets/${presetId}`);
      setPresets((prev) => prev.filter((p) => p.id !== presetId));
      toast.success("Vorlage gelöscht");
    } catch (err) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  // ───── Auto-detect brands in description ─────
  const detectionText = `${briefing.description} ${briefing.painpoints} ${briefing.solution_promise}`;
  const { matches: detectedMatches, suggestedNewSlugs: detectedNew } = useBrandDetection(detectionText, selectedBrandSlugs);

  // ───── Step 1 → Step 2: Generate covers ─────
  const startSession = async () => {
    if (!briefing.description.trim()) {
      toast.error("Bitte gib eine Beschreibung ein");
      return;
    }
    setLoadingCovers(true);
    try {
      const res = await axios.post(`${API}/carousel/start-session`, { briefing });
      setSessionId(res.data.session_id);
      setCoverVariants(res.data.cover_variants || []);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover-Generierung fehlgeschlagen");
    } finally {
      setLoadingCovers(false);
    }
  };

  // ───── Step 2 → Step 3: Pick cover, generate slides ─────
  const selectCover = async () => {
    if (!selectedCoverId) {
      toast.error("Bitte wähle einen Cover-Hook");
      return;
    }
    setLoadingSlides(true);
    try {
      const payload = {
        session_id: sessionId,
        cover_id: selectedCoverId,
      };
      if (customCoverHeadline.trim()) payload.custom_headline = customCoverHeadline.trim();
      if (customCoverSubText) payload.custom_sub_text = customCoverSubText;
      const res = await axios.post(`${API}/carousel/select-cover`, payload);
      setSlides(res.data.slides || []);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Story-Generierung fehlgeschlagen");
    } finally {
      setLoadingSlides(false);
    }
  };

  // ───── Slide editing helpers ─────
  const updateSlide = (idx, field, value) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const moveSlide = (idx, dir) => {
    setSlides((prev) => {
      const next = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next.map((s, i) => ({ ...s, slide_number: i + 1 }));
    });
  };
  const deleteSlide = (idx) => {
    if (slides.length <= 2) {
      toast.error("Mindestens 2 Slides erforderlich");
      return;
    }
    setSlides((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, slide_number: i + 1 })));
  };
  const addSlide = () => {
    if (slides.length >= 10) {
      toast.error("Maximal 10 Slides erlaubt");
      return;
    }
    setSlides((prev) => [
      ...prev,
      {
        slide_number: prev.length + 1,
        slide_type: "story",
        headline: "Neue Slide",
        sub_text: "",
        image_brief: "",
      },
    ]);
  };

  // ───── Step 3 → Step 4: Persist edited slides ─────
  const continueToVisualConfig = async () => {
    try {
      await axios.post(`${API}/carousel/update-slides`, { session_id: sessionId, slides });
      setStep(4);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  // ───── Step 4 → Generate ─────
  const handleGenerate = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/carousel/generate`, {
        session_id: sessionId,
        slides,
        aspect_ratio: aspectRatio,
        brand_colors: brandColors.filter(Boolean),
        selected_persons: selectedPersonId ? [selectedPersonId] : [],
        selected_logos: selectedLogos,
        selected_brand_slugs: selectedBrandSlugs,
        logo_position: "top-right",
        variations: 1,
        quick_variations: quickVariations,
      });
      const variantCount = res.data.variant_count || 1;
      toast.success(
        variantCount > 1
          ? `${variantCount} Karussell-Varianten werden parallel generiert (A/B/C-Test)...`
          : `Karussell wird generiert (${slides.length} Slides)...`
      );
      // Navigate to jobs; primary job auto-opens via ?openJob
      navigate(`/jobs?new=${res.data.job_id}&openJob=${res.data.job_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  // ───── UI render ─────
  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="carousel-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="w-6 h-6 text-primary" />
            Karussell-Ad Generator
          </h1>
          <p className="text-sm text-muted-foreground">
            Multi-Slide-Stories für Instagram + Facebook — optimiert für Andromeda
          </p>
        </div>
        {step === 1 && presets.length > 0 && (
          <Button
            variant={showPresets ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPresets((v) => !v)}
            data-testid="carousel-presets-toggle"
          >
            <BookmarkPlus className="w-4 h-4 mr-2" />
            Vorlagen ({presets.length})
          </Button>
        )}
        <Badge variant="outline">Schritt {step} / 4</Badge>
      </div>

      {/* Presets Panel */}
      {step === 1 && showPresets && presets.length > 0 && (
        <Card className="border-primary/30" data-testid="carousel-presets-panel">
          <CardContent className="pt-4">
            <p className="text-xs font-medium mb-2 text-muted-foreground">
              Klicke auf eine Vorlage, um Briefing + Design vorauszufüllen
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="group flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-left hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid={`carousel-preset-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {(p.wizard_data?.briefing?.description || "—").slice(0, 60)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDeletePreset(p.id, e)}
                    className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                    data-testid={`carousel-preset-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Step1Briefing
          briefing={briefing}
          setBriefing={setBriefing}
          brandKits={brandKits}
          selectedBrandKit={selectedBrandKit}
          onApplyBrandKit={applyBrandKit}
          onNext={startSession}
          loading={loadingCovers}
        />
      )}

      {step === 2 && (
        <Step2CoverSelection
          coverVariants={coverVariants}
          selectedCoverId={selectedCoverId}
          setSelectedCoverId={setSelectedCoverId}
          customHeadline={customCoverHeadline}
          setCustomHeadline={setCustomCoverHeadline}
          customSubText={customCoverSubText}
          setCustomSubText={setCustomCoverSubText}
          onBack={() => setStep(1)}
          onNext={selectCover}
          loading={loadingSlides}
        />
      )}

      {step === 3 && (
        <Step3SlideEdit
          slides={slides}
          onUpdate={updateSlide}
          onMove={moveSlide}
          onDelete={deleteSlide}
          onAdd={addSlide}
          logos={logos}
          authToken={authToken}
          onBack={() => setStep(2)}
          onNext={continueToVisualConfig}
        />
      )}

      {step === 4 && (
        <Step4VisualConfig
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          brandColors={brandColors}
          setBrandColors={setBrandColors}
          persons={persons}
          selectedPersonId={selectedPersonId}
          setSelectedPersonId={setSelectedPersonId}
          logos={logos}
          selectedLogos={selectedLogos}
          setSelectedLogos={setSelectedLogos}
          selectedBrandSlugs={selectedBrandSlugs}
          setSelectedBrandSlugs={setSelectedBrandSlugs}
          onOpenBrandPicker={() => setBrandPickerOpen(true)}
          detectedNew={detectedNew}
          detectedMatches={detectedMatches}
          quickVariations={quickVariations}
          setQuickVariations={setQuickVariations}
          slides={slides}
          briefing={briefing}
          setBriefing={setBriefing}
          brandKits={brandKits}
          selectedBrandKit={selectedBrandKit}
          onApplyBrandKit={applyBrandKit}
          onBack={() => setStep(3)}
          onSubmit={handleGenerate}
          onSaveAsPreset={() => {
            setPresetName(briefing.description?.slice(0, 50) || "Karussell-Vorlage");
            setSavePresetOpen(true);
          }}
          submitting={submitting}
          slideCount={slides.length}
        />
      )}

      <BrandLogoPickerDialog
        open={brandPickerOpen}
        onOpenChange={setBrandPickerOpen}
        selectedSlugs={selectedBrandSlugs}
        onChange={(slugs) => {
          // Carousels carry exactly one company logo — replace, not add
          setSelectedBrandSlugs(slugs.slice(-1));
        }}
        maxSelection={1}
      />

      {/* Save-Preset Dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Karussell-Vorlage speichern</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Name der Vorlage</Label>
            <Input
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="z.B. RAN Recruiting · Physiotherapie"
              autoFocus
              data-testid="carousel-preset-name-input"
            />
            <p className="text-xs text-muted-foreground">
              Briefing + Bildanweisungen + Farben + Person + Logo werden gespeichert. Beim nächsten Mal lädst du sie mit einem Klick und kannst kleine Varianten erstellen.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSavePreset} disabled={savingPreset || !presetName.trim()} data-testid="carousel-preset-save-btn">
              {savingPreset ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 1: Briefing
// ═══════════════════════════════════════════════════════════════════════════
const Step1Briefing = ({ briefing, setBriefing, brandKits, selectedBrandKit, onApplyBrandKit, onNext, loading }) => {
  const upd = (k, v) => setBriefing((prev) => ({ ...prev, [k]: v }));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Worum geht's?</CardTitle>
        <CardDescription>
          Erzähl die Story. Die KI baut darauf 3 Cover-Hooks für dich.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Brand Kit Quick-Apply */}
        {Array.isArray(brandKits) && brandKits.length > 0 && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="carousel-brand-kit-section">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Brand Kit anwenden</span>
              <span className="text-[11px] text-muted-foreground">(Farben + Bildwünsche + CTA)</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {brandKits.map((kit) => {
                const isSel = selectedBrandKit?.id === kit.id;
                return (
                  <button
                    key={kit.id}
                    type="button"
                    onClick={() => onApplyBrandKit(kit)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all hover:border-primary hover:bg-primary/5 ${
                      isSel ? "border-primary bg-primary/5 font-medium" : ""
                    }`}
                    data-testid={`carousel-brand-kit-${kit.id}`}
                  >
                    {kit.colors?.[0]?.hex && (
                      <span
                        className="h-3 w-3 rounded-full border"
                        style={{ backgroundColor: kit.colors[0].hex }}
                      />
                    )}
                    {kit.name}
                    {isSel && <Check className="h-3 w-3 text-primary" />}
                  </button>
                );
              })}
            </div>
            {selectedBrandKit && (
              <p className="text-[11px] text-muted-foreground">
                „{selectedBrandKit.name}" angewendet — Farben, Bildwünsche, CTA und Tonalität vorausgefüllt.
              </p>
            )}
          </div>
        )}

        {/* Ad type pills */}
        <div className="space-y-2">
          <Label>Ad-Typ</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {AD_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => upd("ad_type", t.value)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  briefing.ad_type === t.value
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-border hover:bg-accent/40"
                }`}
                data-testid={`carousel-ad-type-${t.value}`}
              >
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-[11px] text-muted-foreground">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tone */}
        <div className="space-y-2">
          <Label>Tonalität</Label>
          <div className="grid grid-cols-3 gap-2">
            {TONE_OPTIONS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => upd("tone", t.value)}
                className={`rounded-lg border p-3 text-center transition-colors ${
                  briefing.tone === t.value
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-border hover:bg-accent/40"
                }`}
                data-testid={`carousel-tone-${t.value}`}
              >
                <div className="text-xl">{t.emoji}</div>
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-[10px] text-muted-foreground">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Zielgruppe</Label>
          <Input
            placeholder='z.B. "Physiotherapeut:innen 25-45 Jahre"'
            value={briefing.target_audience}
            onChange={(e) => upd("target_audience", e.target.value)}
            data-testid="carousel-target-audience"
          />
        </div>

        {briefing.ad_type === "recruiting" && (
          <div className="space-y-2">
            <Label>Gesuchter Beruf (für Gendering)</Label>
            <Input
              placeholder='z.B. "Physiotherapeut"'
              value={briefing.job_role}
              onChange={(e) => upd("job_role", e.target.value)}
              data-testid="carousel-job-role"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Beschreibung / Story (das Wichtigste!)</Label>
          <Textarea
            rows={5}
            placeholder="Erzähl die ganze Geschichte. Z.B.: 'Wir suchen Physiotherapeuten. 4000€ Gehalt, kostenlose Weiterbildung, Fahrgeld. Zwei Wege: unbefristet oder Nebenjob 38€/h...'"
            value={briefing.description}
            onChange={(e) => upd("description", e.target.value)}
            data-testid="carousel-description"
          />
          <p className="text-[11px] text-muted-foreground">
            Tipp: Je konkreter, desto besser die Story-Generierung. Nutze Zahlen, klare Vorteile, die Wahl-Möglichkeiten.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Painpoints (was nervt deine Zielgruppe?)</Label>
            <Textarea
              rows={3}
              placeholder="z.B. zu wenig Geld, keine Wertschätzung..."
              value={briefing.painpoints}
              onChange={(e) => upd("painpoints", e.target.value)}
              data-testid="carousel-painpoints"
            />
          </div>
          <div className="space-y-2">
            <Label>Versprechen / Lösung</Label>
            <Textarea
              rows={3}
              placeholder="z.B. faires Gehalt, freie Weiterbildung, flexible Modelle"
              value={briefing.solution_promise}
              onChange={(e) => upd("solution_promise", e.target.value)}
              data-testid="carousel-solution"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Bildanweisungen <span className="text-[11px] text-muted-foreground font-normal">(Authentizität, gilt für alle Slides)</span>
          </Label>
          <Textarea
            rows={3}
            placeholder='z.B. "Achte darauf, dass die Person als authentische Physiotherapeutin in einem realistischen Praxisumfeld dargestellt wird. Keine gestellten Stockfotos."'
            value={briefing.image_context || ""}
            onChange={(e) => upd("image_context", e.target.value)}
            data-testid="carousel-image-context"
          />
          <p className="text-[11px] text-muted-foreground">
            Diese Anweisung wird in jeden Slide-Prompt eingebettet — sorgt für authentische Personen & realistische Umgebung auf allen Bildern.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Call-to-Action</Label>
          <Input
            placeholder="z.B. Jetzt bewerben, Mehr erfahren, Termin sichern"
            value={briefing.cta}
            onChange={(e) => upd("cta", e.target.value)}
            data-testid="carousel-cta"
          />
        </div>

        <div className="pt-2 flex justify-end">
          <Button
            onClick={onNext}
            disabled={loading || !briefing.description.trim()}
            data-testid="carousel-step1-next"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                KI baut Cover-Hooks...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Cover-Hooks generieren
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Step 2: Cover selection
// ═══════════════════════════════════════════════════════════════════════════
const Step2CoverSelection = ({
  coverVariants,
  selectedCoverId,
  setSelectedCoverId,
  customHeadline,
  setCustomHeadline,
  customSubText,
  setCustomSubText,
  onBack,
  onNext,
  loading,
}) => {
  const selected = coverVariants.find((c) => c.id === selectedCoverId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wähle deinen Cover-Hook</CardTitle>
        <CardDescription>
          Die KI hat 3 verschiedene Anker-Strategien gebaut. Diese erste Slide entscheidet, ob jemand swiped — wähle die stärkste für deine Zielgruppe.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3">
          {coverVariants.map((cv) => (
            <button
              key={cv.id}
              type="button"
              onClick={() => {
                setSelectedCoverId(cv.id);
                setCustomHeadline("");
                setCustomSubText("");
              }}
              className={`text-left rounded-lg border p-4 transition-all ${
                selectedCoverId === cv.id
                  ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                  : "border-border hover:bg-accent/40"
              }`}
              data-testid={`carousel-cover-${cv.angle}`}
            >
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  {selectedCoverId === cv.id ? (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-muted" />
                  )}
                </div>
                <div className="flex-1">
                  <Badge variant="outline" className="text-[10px] mb-2">
                    {cv.label}
                  </Badge>
                  <p className="font-semibold text-lg leading-tight">{cv.headline}</p>
                  {cv.sub_text && (
                    <p className="text-sm text-muted-foreground mt-1">{cv.sub_text}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-2 italic">
                    Style: {cv.style_hint}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {selected && (
          <Card className="border-dashed">
            <CardContent className="pt-4 space-y-3">
              <p className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Optional: Headline anpassen
              </p>
              <Input
                placeholder={selected.headline}
                value={customHeadline}
                onChange={(e) => setCustomHeadline(e.target.value)}
                data-testid="carousel-custom-headline"
              />
              <Input
                placeholder={selected.sub_text || "Sub-Text..."}
                value={customSubText}
                onChange={(e) => setCustomSubText(e.target.value)}
                data-testid="carousel-custom-subtext"
              />
              <p className="text-[11px] text-muted-foreground">
                Leer lassen, um die KI-Variante 1:1 zu übernehmen.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <Button onClick={onNext} disabled={!selectedCoverId || loading} data-testid="carousel-step2-next">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                KI baut Story-Slides...
              </>
            ) : (
              <>
                Story bauen
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Step 3: Slide editing
// ═══════════════════════════════════════════════════════════════════════════
const SLIDE_TYPE_LABELS = {
  cover: { label: "Cover", emoji: "🎯", color: "bg-primary/10 border-primary/30" },
  story: { label: "Story", emoji: "📖", color: "bg-blue-500/5 border-blue-500/20" },
  list: { label: "Bullet-Liste", emoji: "✅", color: "bg-green-500/5 border-green-500/20" },
  cta: { label: "Call-to-Action", emoji: "🎬", color: "bg-orange-500/5 border-orange-500/20" },
};

// Curated list of contact/messenger brand-slugs offered as quick picks
// for the per-slide "extra logo" field (e.g. WhatsApp on the CTA slide).
const SLIDE_CONTACT_BRANDS = [
  { slug: "whatsapp", label: "WhatsApp" },
  { slug: "telegram", label: "Telegram" },
  { slug: "instagram", label: "Instagram" },
  { slug: "facebook", label: "Facebook" },
  { slug: "linkedin", label: "LinkedIn" },
  { slug: "gmail", label: "Gmail" },
  { slug: "phone", label: "Phone" },
];

const SlideExtraLogoPicker = ({ slide, slideIdx, logos, authToken, onUpdate }) => {
  const apiBase = API;
  const hasExtra = !!(slide.extra_logo_id || slide.extra_logo_brand_slug);
  const [open, setOpen] = useState(false);

  const clear = () => {
    onUpdate(slideIdx, "extra_logo_id", null);
    onUpdate(slideIdx, "extra_logo_brand_slug", null);
    onUpdate(slideIdx, "extra_logo_label", null);
  };

  const pickBrand = (slug, label) => {
    onUpdate(slideIdx, "extra_logo_id", null);
    onUpdate(slideIdx, "extra_logo_brand_slug", slug);
    onUpdate(slideIdx, "extra_logo_label", label);
    setOpen(false);
  };

  const pickCustom = (logo) => {
    onUpdate(slideIdx, "extra_logo_id", logo.id);
    onUpdate(slideIdx, "extra_logo_brand_slug", null);
    onUpdate(slideIdx, "extra_logo_label", logo.name || "Logo");
    setOpen(false);
  };

  const currentImg = slide.extra_logo_id
    ? `${apiBase}/logos/${slide.extra_logo_id}/image${authToken ? `?token=${authToken}` : ""}`
    : slide.extra_logo_brand_slug
      ? `${apiBase}/brand-logos/${slide.extra_logo_brand_slug}/image`
      : null;

  return (
    <div className="pt-1 border-t border-border/60">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" />
          Zusatz-Logo nur für diesen Slide
          <span className="text-[10px] text-muted-foreground font-normal">
            (z.B. WhatsApp auf der CTA-Slide)
          </span>
        </Label>
        {hasExtra && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
            onClick={clear}
            data-testid={`carousel-slide-extra-logo-clear-${slideIdx + 1}`}
          >
            <X className="w-3 h-3 mr-1" /> Entfernen
          </Button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 w-full justify-start gap-2"
            data-testid={`carousel-slide-extra-logo-${slideIdx + 1}`}
          >
            {currentImg ? (
              <>
                <img src={currentImg} alt="" className="h-5 w-5 object-contain" />
                <span className="text-xs">{slide.extra_logo_label || "Zusatz-Logo"}</span>
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" />
                <span className="text-xs text-muted-foreground">Zusatz-Logo wählen (optional)</span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-3" align="start">
          <div className="space-y-3">
            {/* Quick contact brands */}
            <div>
              <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
                Bekannte Kontakt-Marken
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {SLIDE_CONTACT_BRANDS.map((b) => {
                  const isSel = slide.extra_logo_brand_slug === b.slug;
                  return (
                    <button
                      key={b.slug}
                      type="button"
                      onClick={() => pickBrand(b.slug, b.label)}
                      className={`flex flex-col items-center gap-1 rounded-md border p-1.5 hover:border-primary hover:bg-primary/5 transition-colors ${
                        isSel ? "border-primary bg-primary/10" : ""
                      }`}
                      data-testid={`slide-${slideIdx + 1}-brand-${b.slug}`}
                      title={b.label}
                    >
                      <img
                        src={`${apiBase}/brand-logos/${b.slug}/image`}
                        alt={b.label}
                        className="h-6 w-6 object-contain"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <span className="text-[10px] truncate w-full text-center">{b.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User custom logos */}
            {logos.length > 0 && (
              <div>
                <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
                  Aus deiner Logo-Bibliothek
                </p>
                <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto">
                  {logos.map((logo) => {
                    const isSel = slide.extra_logo_id === logo.id;
                    return (
                      <button
                        key={logo.id}
                        type="button"
                        onClick={() => pickCustom(logo)}
                        className={`flex flex-col items-center gap-1 rounded-md border p-1.5 hover:border-primary hover:bg-primary/5 transition-colors ${
                          isSel ? "border-primary bg-primary/10" : ""
                        }`}
                        data-testid={`slide-${slideIdx + 1}-custom-${logo.id}`}
                        title={logo.name}
                      >
                        <img
                          src={`${apiBase}/logos/${logo.id}/image${authToken ? `?token=${authToken}` : ""}`}
                          alt={logo.name}
                          className="h-6 w-6 object-contain"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <span className="text-[10px] truncate w-full text-center">{logo.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground italic">
              Wird zusätzlich zum Haupt-Brand-Logo auf nur dieser Slide gerendert.
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const Step3SlideEdit = ({ slides, onUpdate, onMove, onDelete, onAdd, logos = [], authToken, onBack, onNext }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Story bearbeiten ({slides.length} Slides)</CardTitle>
        <CardDescription>
          Hier kannst du jeden Slide-Text anpassen, Slides hinzufügen/löschen oder umsortieren. Empfohlen: 5-7 Slides.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {slides.map((slide, idx) => {
          const typeInfo = SLIDE_TYPE_LABELS[slide.slide_type] || SLIDE_TYPE_LABELS.story;
          return (
            <Card
              key={idx}
              className={`${typeInfo.color} border-2`}
              data-testid={`carousel-slide-${idx + 1}`}
            >
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Badge>
                    {typeInfo.emoji} Slide {idx + 1} · {typeInfo.label}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onMove(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`carousel-slide-up-${idx + 1}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onMove(idx, 1)}
                      disabled={idx === slides.length - 1}
                      data-testid={`carousel-slide-down-${idx + 1}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => onDelete(idx)}
                      data-testid={`carousel-slide-delete-${idx + 1}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Headline</Label>
                  <Input
                    value={slide.headline}
                    onChange={(e) => onUpdate(idx, "headline", e.target.value)}
                    className="font-semibold"
                    data-testid={`carousel-slide-headline-${idx + 1}`}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">
                      Sub-Text
                      <span className="text-[10px] text-muted-foreground font-normal ml-1">
                        (optional — leer lassen, wenn nur Bullets gewünscht)
                      </span>
                    </Label>
                    {slide.sub_text && slide.sub_text.trim() && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => onUpdate(idx, "sub_text", "")}
                        data-testid={`carousel-slide-subtext-clear-${idx + 1}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Entfernen
                      </Button>
                    )}
                  </div>
                  <Textarea
                    rows={2}
                    placeholder="Optional — leer lassen, wenn dieser Slide nur Headline + Bullets haben soll"
                    value={slide.sub_text || ""}
                    onChange={(e) => onUpdate(idx, "sub_text", e.target.value)}
                    data-testid={`carousel-slide-subtext-${idx + 1}`}
                  />
                </div>
                {/* Bullet-Points: verfügbar für ALLE Slide-Typen */}
                <div data-testid={`carousel-bullets-section-${idx + 1}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">
                      Bullet-Punkte
                      <span className="text-[10px] text-muted-foreground font-normal ml-1">
                        (optional, max 5 — Icon klickbar)
                      </span>
                    </Label>
                    {(!slide.bullet_points || slide.bullet_points.length < 5) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => {
                          // Inherit the icon from the last bullet so a list stays
                          // visually consistent (e.g. all ❌ on a painpoint slide).
                          const prev = slide.bullet_points || [];
                          const lastIcon = prev.length > 0 ? extractBulletIcon(prev[prev.length - 1]) : "✅";
                          const next = [...prev, `${lastIcon} `];
                          onUpdate(idx, "bullet_points", next);
                        }}
                        data-testid={`carousel-slide-bullet-add-${idx + 1}`}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Bullet hinzufügen
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {(slide.bullet_points || []).map((bp, bi) => {
                      const currentIcon = extractBulletIcon(bp);
                      const restText = stripBulletIcon(bp);
                      return (
                        <div key={bi} className="flex items-center gap-1">
                          {/* Icon-Picker (Popover) */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-9 shrink-0 text-base"
                                data-testid={`carousel-slide-bullet-icon-${idx + 1}-${bi}`}
                                title="Icon ändern (✅ positiv / ❌ negativ / ⚠️ Warnung etc.)"
                              >
                                {currentIcon}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-1.5" align="start">
                              <div className="grid grid-cols-6 gap-1" data-testid={`carousel-slide-bullet-icon-picker-${idx + 1}-${bi}`}>
                                {BULLET_ICONS.map((ic) => (
                                  <button
                                    key={ic.emoji}
                                    type="button"
                                    onClick={() => {
                                      const next = [...(slide.bullet_points || [])];
                                      next[bi] = `${ic.emoji} ${restText}`.trimEnd();
                                      onUpdate(idx, "bullet_points", next);
                                    }}
                                    className={`h-8 w-8 rounded-md text-base hover:bg-muted transition-colors ${
                                      currentIcon === ic.emoji ? "bg-primary/10 ring-1 ring-primary" : ""
                                    }`}
                                    title={ic.label}
                                    data-testid={`bullet-icon-${ic.emoji}`}
                                  >
                                    {ic.emoji}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Input
                            value={restText}
                            placeholder={currentIcon === "❌" ? "z.B. Stress im Alltag" : "z.B. 4-Tage-Woche"}
                            onChange={(e) => {
                              const next = [...(slide.bullet_points || [])];
                              next[bi] = `${currentIcon} ${e.target.value}`;
                              onUpdate(idx, "bullet_points", next);
                            }}
                            data-testid={`carousel-slide-bullet-${idx + 1}-${bi}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              const next = (slide.bullet_points || []).filter((_, j) => j !== bi);
                              onUpdate(idx, "bullet_points", next.length ? next : null);
                            }}
                            data-testid={`carousel-slide-bullet-delete-${idx + 1}-${bi}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                    {(!slide.bullet_points || slide.bullet_points.length === 0) && (
                      <p className="text-[11px] text-muted-foreground italic">
                        Keine Bullets — klicke „Bullet hinzufügen", um eigene Stichpunkte zu ergänzen.
                      </p>
                    )}
                  </div>
                </div>

                {/* Per-slide Extra-Logo (z.B. WhatsApp auf der CTA-Slide) */}
                <SlideExtraLogoPicker
                  slide={slide}
                  slideIdx={idx}
                  logos={logos}
                  authToken={authToken}
                  onUpdate={onUpdate}
                />
              </CardContent>
            </Card>
          );
        })}

        <Button variant="outline" className="w-full" onClick={onAdd} data-testid="carousel-add-slide">
          <Plus className="w-4 h-4 mr-2" />
          Slide hinzufügen
        </Button>

        <div className="flex justify-between pt-2">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <Button onClick={onNext} data-testid="carousel-step3-next">
            Weiter zum Design
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Step 4: Visual config + generate
// ═══════════════════════════════════════════════════════════════════════════
const Step4VisualConfig = ({
  aspectRatio,
  setAspectRatio,
  brandColors,
  setBrandColors,
  persons,
  selectedPersonId,
  setSelectedPersonId,
  logos,
  selectedLogos,
  setSelectedLogos,
  selectedBrandSlugs,
  setSelectedBrandSlugs,
  onOpenBrandPicker,
  detectedNew,
  detectedMatches,
  quickVariations,
  setQuickVariations,
  slides,
  briefing,
  setBriefing,
  brandKits,
  selectedBrandKit,
  onApplyBrandKit,
  onBack,
  onSubmit,
  onSaveAsPreset,
  submitting,
  slideCount,
}) => {
  const suggestedBrands = (detectedMatches || []).filter((m) => detectedNew?.includes(m.slug));
  const authToken = localStorage.getItem("auth_token") || "";

  // Logo handling: at most ONE logo total (either eigene OR brand library)
  const toggleLogo = (id) => {
    if (selectedLogos.includes(id)) {
      setSelectedLogos([]);
    } else {
      setSelectedLogos([id]);
      // selecting eigenes logo wipes brand-library logo so we only ever have 1
      if (selectedBrandSlugs.length > 0) setSelectedBrandSlugs([]);
    }
  };

  // Andromeda score (live)
  const andromeda = useMemo(
    () => computeAndromedaScore({
      slides,
      brandColors,
      selectedPersonId,
      hasLogoConfigured: selectedLogos.length + selectedBrandSlugs.length > 0,
    }),
    [slides, brandColors, selectedPersonId, selectedLogos, selectedBrandSlugs]
  );
  const scoreColor = getScoreColor(andromeda.score);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Design & Konsistenz</CardTitle>
        <CardDescription>
          Format, Farben, Person, Logos — diese Einstellungen gelten für ALLE {slideCount} Slides (Andromeda liebt Konsistenz).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Bildanweisungen (last-chance edit before generation) */}
        {briefing && setBriefing && (
          <div className="space-y-2" data-testid="carousel-step4-image-context">
            <Label className="flex items-center gap-2">
              Bildanweisungen <span className="text-[11px] text-muted-foreground font-normal">(gilt für alle Slides)</span>
            </Label>
            <Textarea
              rows={2}
              placeholder='z.B. "authentische Physiotherapeutin in realistischem Praxisumfeld"'
              value={briefing.image_context || ""}
              onChange={(e) => setBriefing((prev) => ({ ...prev, image_context: e.target.value }))}
              data-testid="carousel-step4-image-context-input"
            />
          </div>
        )}

        {/* Safe-Zone-Info */}
        <div className="rounded-lg border bg-primary/5 border-primary/30 p-3 flex items-start gap-2" data-testid="carousel-safe-zone-info">
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Safe-Zones aktiv ({aspectRatio}):</span> Headlines, Texte & Gesichter werden automatisch aus den Bereichen herausgehalten, in denen Instagram & Facebook ihre eigene UI (Username, CTA-Button, Reaktionen, Karussell-Dots) über das Bild legen. So bleibt alles Wichtige im Feed sichtbar.
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="space-y-2">
          <Label>Format</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAspectRatio("4:5")}
              className={`rounded-lg border-2 p-4 text-left transition-colors ${
                aspectRatio === "4:5"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-accent/40"
              }`}
              data-testid="carousel-aspect-4-5"
            >
              <RectangleVertical className="w-5 h-5 mb-1 text-primary" />
              <div className="font-medium">4:5 Hochformat</div>
              <div className="text-[11px] text-muted-foreground">
                Instagram-Empfehlung • 1080×1350 • mobile-optimiert
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAspectRatio("1:1")}
              className={`rounded-lg border-2 p-4 text-left transition-colors ${
                aspectRatio === "1:1"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-accent/40"
              }`}
              data-testid="carousel-aspect-1-1"
            >
              <Square className="w-5 h-5 mb-1 text-primary" />
              <div className="font-medium">1:1 Quadrat</div>
              <div className="text-[11px] text-muted-foreground">
                Klassisch • 1080×1080
              </div>
            </button>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="flex items-center gap-2">
              Markenfarben <span className="text-[11px] text-muted-foreground font-normal">(mind. 2)</span>
            </Label>
            {Array.isArray(brandKits) && brandKits.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="carousel-step4-brand-kit-row">
                <Package className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-[11px] text-muted-foreground mr-1">Brand Kit:</span>
                {brandKits.map((kit) => {
                  const isSel = selectedBrandKit?.id === kit.id;
                  const swatches = (kit.colors || []).slice(0, 4);
                  return (
                    <button
                      key={kit.id}
                      type="button"
                      onClick={() => onApplyBrandKit && onApplyBrandKit(kit)}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] transition-all hover:border-primary hover:bg-primary/5 ${
                        isSel ? "border-primary bg-primary/5 font-medium" : ""
                      }`}
                      data-testid={`carousel-step4-brand-kit-${kit.id}`}
                      title={`Brand Kit „${kit.name}" anwenden`}
                    >
                      <span className="flex -space-x-1">
                        {swatches.map((sw, i) => (
                          <span
                            key={i}
                            className="h-3 w-3 rounded-full border border-background"
                            style={{ backgroundColor: sw.hex || sw }}
                          />
                        ))}
                      </span>
                      {kit.name}
                      {isSel && <Check className="h-3 w-3 text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Brand Kit color picker — individually toggleable */}
          {selectedBrandKit && Array.isArray(selectedBrandKit.colors) && selectedBrandKit.colors.length > 0 && (
            <div className="space-y-2 rounded-lg border bg-muted/20 p-3" data-testid="carousel-bk-color-picker">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">
                  Farben aus „{selectedBrandKit.name}" — klicken zum An-/Abwählen:
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[11px]"
                  onClick={() => {
                    const allHex = (selectedBrandKit.colors || []).map((c) => c.hex || c).filter(Boolean);
                    setBrandColors(allHex.slice(0, 5));
                  }}
                  data-testid="carousel-bk-apply-all"
                >
                  Alle übernehmen
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(selectedBrandKit.colors || []).map((c, idx) => {
                  const hex = (c.hex || c || "").toUpperCase();
                  const label = c.name || c.role || "";
                  const isActive = brandColors.map((bc) => (bc || "").toUpperCase()).includes(hex);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if (isActive) {
                          // remove this color
                          const next = brandColors.filter((bc) => (bc || "").toUpperCase() !== hex);
                          if (next.length >= 2) setBrandColors(next);
                          else toast.error("Mindestens 2 Farben benötigt");
                        } else {
                          // add this color (max 5)
                          if (brandColors.length >= 5) {
                            toast.error("Maximal 5 Farben");
                            return;
                          }
                          setBrandColors([...brandColors, hex]);
                        }
                      }}
                      className={`group relative flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border-2 transition-all hover:scale-105 ${
                        isActive
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-muted hover:border-primary/40"
                      }`}
                      data-testid={`carousel-bk-color-${idx}`}
                      title={`${hex}${label ? ` · ${label}` : ""} — ${isActive ? "Entfernen" : "Hinzufügen"}`}
                    >
                      <span
                        className="h-8 w-8 rounded-md border shadow-sm"
                        style={{ backgroundColor: hex }}
                      />
                      <span className="text-[10px] font-mono text-muted-foreground">{hex}</span>
                      {isActive && (
                        <Check className="absolute -top-1 -right-1 h-4 w-4 text-primary bg-background rounded-full p-0.5 border border-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-muted-foreground border-t">
                <label className="flex items-center gap-1.5 cursor-pointer" title="Tonalität aus dem Brand Kit übernehmen">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={briefing?.tone === selectedBrandKit.tone}
                    onChange={(e) => {
                      if (e.target.checked && selectedBrandKit.tone) {
                        setBriefing((prev) => ({ ...prev, tone: selectedBrandKit.tone.toLowerCase() }));
                      }
                    }}
                    data-testid="carousel-bk-apply-tone"
                  />
                  Tonalität „{selectedBrandKit.tone || "—"}" anwenden
                </label>
                {selectedBrandKit.image_preferences && (
                  <label className="flex items-center gap-1.5 cursor-pointer" title="Bildwünsche aus dem Brand Kit übernehmen">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={(briefing?.image_context || "").includes(selectedBrandKit.image_preferences)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBriefing((prev) => ({ ...prev, image_context: selectedBrandKit.image_preferences }));
                        } else {
                          setBriefing((prev) => ({ ...prev, image_context: "" }));
                        }
                      }}
                      data-testid="carousel-bk-apply-imgctx"
                    />
                    Bildwünsche übernehmen
                  </label>
                )}
                {selectedBrandKit.default_cta && (
                  <label className="flex items-center gap-1.5 cursor-pointer" title="Standard-CTA aus dem Brand Kit übernehmen">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={briefing?.cta === selectedBrandKit.default_cta}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBriefing((prev) => ({ ...prev, cta: selectedBrandKit.default_cta }));
                        }
                      }}
                      data-testid="carousel-bk-apply-cta"
                    />
                    CTA „{selectedBrandKit.default_cta}" anwenden
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {brandColors.map((c, i) => (
              <div key={i} className="flex items-center gap-1">
                <input
                  type="color"
                  value={c || "#000000"}
                  onChange={(e) => {
                    const next = [...brandColors];
                    next[i] = e.target.value;
                    setBrandColors(next);
                  }}
                  className="w-10 h-10 rounded-md border cursor-pointer"
                  data-testid={`carousel-color-${i}`}
                />
                <Input
                  value={c}
                  onChange={(e) => {
                    const next = [...brandColors];
                    next[i] = e.target.value;
                    setBrandColors(next);
                  }}
                  className="w-24 text-xs"
                />
                {brandColors.length > 2 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setBrandColors(brandColors.filter((_, j) => j !== i))}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
            {brandColors.length < 5 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBrandColors([...brandColors, "#FFFFFF"])}
              >
                <Plus className="w-3 h-3 mr-1" />
                Farbe
              </Button>
            )}
          </div>
          {selectedBrandKit && (
            <p className="text-[11px] text-muted-foreground">
              „{selectedBrandKit.name}" angewendet — Farben + Bildanweisungen + CTA übernommen.
            </p>
          )}
        </div>

        {/* Person (Identity-Lock) */}
        {persons.length > 0 && (
          <div className="space-y-2">
            <Label>
              Person (gleich auf allen Slides — Identity-Lock)
              <span className="text-[11px] text-muted-foreground font-normal ml-1">
                {persons.length} verfügbar — horizontal scrollen
              </span>
            </Label>
            <ScrollArea className="w-full whitespace-nowrap rounded-md">
              <div className="flex gap-2 pb-3">
                <button
                  type="button"
                  onClick={() => setSelectedPersonId(null)}
                  className={`flex-shrink-0 rounded-lg border-2 p-2 ${
                    !selectedPersonId
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  }`}
                  data-testid="carousel-person-none"
                >
                  <div className="w-14 h-14 bg-muted rounded flex items-center justify-center text-xs">
                    Keine
                  </div>
                </button>
                {persons.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPersonId(p.id)}
                    className={`flex-shrink-0 rounded-lg border-2 p-2 ${
                      selectedPersonId === p.id
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-border"
                    }`}
                    data-testid={`carousel-person-${p.id}`}
                  >
                    <div className="w-14 h-14 bg-muted rounded overflow-hidden">
                      <img
                        src={`${API}/persons/${p.id}/image${authToken ? `?token=${authToken}` : ''}`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                    <p className="text-[10px] mt-1 truncate w-14">{p.name}</p>
                  </button>
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {/* Eigene Logos */}
        {logos.length > 0 && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Eigenes Logo {selectedLogos.length > 0 && <Badge variant="secondary">1</Badge>}
            </Label>
            <ScrollArea className="w-full whitespace-nowrap rounded-md">
              <div className="flex gap-2 pb-3">
                <button
                  type="button"
                  onClick={() => setSelectedLogos([])}
                  className={`flex-shrink-0 rounded-lg border-2 p-2 transition-colors ${
                    selectedLogos.length === 0 && selectedBrandSlugs.length === 0
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-accent/40"
                  }`}
                  data-testid="carousel-logo-none"
                >
                  <div className="w-14 h-14 bg-muted rounded flex items-center justify-center text-[10px] text-muted-foreground">
                    Kein Logo
                  </div>
                  <p className="text-[10px] mt-1 text-center w-14">—</p>
                </button>
                {logos.map((logo) => {
                  const isSel = selectedLogos.includes(logo.id);
                  return (
                    <button
                      key={logo.id}
                      type="button"
                      onClick={() => toggleLogo(logo.id)}
                      className={`flex-shrink-0 rounded-lg border-2 p-2 transition-colors ${
                        isSel
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border hover:bg-accent/40"
                      }`}
                      data-testid={`carousel-logo-${logo.id}`}
                    >
                      <div className="w-14 h-14 bg-muted rounded overflow-hidden flex items-center justify-center">
                        <img
                          src={`${API}/logos/${logo.id}/image${authToken ? `?token=${authToken}` : ''}`}
                          alt={logo.name}
                          className="w-full h-full object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                      <p className="text-[10px] mt-1 truncate w-14" title={logo.name}>{logo.name}</p>
                    </button>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <p className="text-[11px] text-muted-foreground">
              Genau 1 Logo aus deiner Bibliothek — oder „Kein Logo".
            </p>
          </div>
        )}

        {/* Brand library */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Bekannte Marken
            </Label>
            <Button variant="outline" size="sm" onClick={onOpenBrandPicker} data-testid="carousel-open-brand-picker">
              <Plus className="w-3 h-3 mr-1" />
              Wählen
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Genau ein Logo (entweder eigenes ODER eine bekannte Marke) — oder gar keins.
          </p>
          {suggestedBrands.length > 0 && (
            <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
              <p className="text-xs mb-1.5">
                <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
                Erkannte Marken in deiner Story:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestedBrands.map((b) => (
                  <button
                    key={b.slug}
                    type="button"
                    onClick={() => {
                      setSelectedBrandSlugs([b.slug]);
                      setSelectedLogos([]);
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-background border border-primary/30 px-2 py-0.5 text-[11px] hover:bg-primary/10"
                  >
                    <BrandLogoImg slug={b.slug} name={b.name} size="w-3 h-3" />
                    ➕ {b.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedBrandSlugs.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedBrandSlugs.map((slug) => (
                <div key={slug} className="flex items-center gap-1.5 rounded-md border bg-card pl-1 pr-2 py-1">
                  <div className="w-5 h-5">
                    <BrandLogoImg slug={slug} name={slug} size="w-5 h-5" />
                  </div>
                  <span className="text-xs">{slug}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedBrandSlugs(selectedBrandSlugs.filter((s) => s !== slug))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Optional — Logos werden klein in der Ecke auf jeder Slide platziert
            </p>
          )}
        </div>

        {/* ─── Andromeda-Score ─── */}
        <div className={`rounded-xl border-2 p-4 ${scoreColor.bg} ${scoreColor.border}`} data-testid="andromeda-score">
          <div className="flex items-center gap-3">
            <div className="relative w-16 h-16 flex items-center justify-center">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/30" />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="6"
                  strokeDasharray={`${(andromeda.score / 100) * 175.93} 175.93`}
                  strokeLinecap="round"
                  className={scoreColor.color}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-xl font-bold ${scoreColor.color}`}>{andromeda.score}</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold flex items-center gap-2">
                Andromeda-Score
                <Badge variant="outline" className={`${scoreColor.color} ${scoreColor.border}`}>
                  {scoreColor.label}
                </Badge>
              </p>
              <p className="text-xs text-muted-foreground">
                Wie gut performt dein Karussell wahrscheinlich im Meta-Andromeda-Ranking
              </p>
            </div>
          </div>

          {andromeda.breakdown.some((b) => b.tip) && (
            <div className="mt-3 space-y-1.5 border-t pt-3 border-current/10">
              <p className="text-xs font-medium">💡 Verbesserungs-Tipps:</p>
              {andromeda.breakdown
                .filter((b) => b.tip)
                .map((b) => (
                  <div key={b.key} className="flex items-start gap-2 text-[11px]">
                    <span className="text-muted-foreground mt-0.5">·</span>
                    <span>
                      <span className="font-medium">{b.label}</span> ({b.points}/{b.max} Pkt): {b.tip}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {/* Full breakdown collapsible */}
          <details className="mt-3 text-xs border-t pt-3 border-current/10">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Alle Faktoren anzeigen ({andromeda.breakdown.length})
            </summary>
            <div className="mt-2 space-y-1">
              {andromeda.breakdown.map((b) => (
                <div key={b.key} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    {b.ok ? (
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-muted-foreground" />
                    )}
                    {b.label}
                  </span>
                  <span className="font-mono tabular-nums">
                    {b.points}/{b.max}
                  </span>
                </div>
              ))}
            </div>
          </details>
        </div>

        {/* ─── Quick-Variations-Modus ─── */}
        <label
          className={`flex items-center gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${
            quickVariations
              ? "border-primary bg-primary/5"
              : "border-border hover:bg-accent/30"
          }`}
          data-testid="carousel-quick-variations-toggle"
        >
          <input
            type="checkbox"
            checked={quickVariations}
            onChange={(e) => setQuickVariations(e.target.checked)}
            className="w-4 h-4 accent-primary"
          />
          <div className="flex-1">
            <p className="text-sm font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Andromeda-Score-Boost-Varianten (A/B/C-Test)
            </p>
            <p className="text-[11px] text-muted-foreground">
              Generiert zusätzlich 2 KI-optimierte Karussells:
              <span className="font-semibold"> B = Viral-Boost</span> (kürzere, zahlengetriebene Hooks) und
              <span className="font-semibold"> C = Storytelling</span> (emotionale Erzähltechnik).
              Beide gezielt auf höheren Andromeda-Score optimiert. Deine Variante A bleibt unverändert.
            </p>
          </div>
        </label>

        <div className="flex flex-wrap justify-between gap-3 pt-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            {onSaveAsPreset && (
              <Button
                variant="outline"
                onClick={onSaveAsPreset}
                data-testid="carousel-save-preset-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Als Vorlage speichern
              </Button>
            )}
          </div>
          <Button onClick={onSubmit} disabled={submitting} data-testid="carousel-generate">
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Karussell wird generiert...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {quickVariations
                  ? `${slideCount * 3} Slides (3 Varianten) generieren`
                  : `${slideCount} Slides generieren`}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
